
import { useTranslation } from 'react-i18next';
import { useScrollAnimation } from '../../../hooks/useScrollAnimation';
import { useState } from 'react';

export default function AboutSection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollAnimation();
  const [activeTab, setActiveTab] = useState('mission');

  const tabs = [
    { id: 'mission', icon: 'ri-heart-line', key: 'mission' },
    { id: 'vision', icon: 'ri-eye-line', key: 'vision' },
    { id: 'values', icon: 'ri-book-open-line', key: 'values' },
    { id: 'history', icon: 'ri-star-line', key: 'history' }
  ];

  return (
    <section 
      id="about" 
      ref={ref}
      className={`py-12 sm:py-16 md:py-24 relative transition-all duration-1000 ${
        isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
      }`}
      style={{
        backgroundImage: `url('https://public.readdy.ai/ai/img_res/6b44160264a310d778b75d7b30bef647.jpg')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Background overlay for better text readability */}
      <div className="absolute inset-0 bg-white/90"></div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12">
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-neutral-900 tracking-tight mb-3 sm:mb-4">
            {t('about.title')}
          </h2>
          <p className="text-sm sm:text-base lg:text-xl text-neutral-600 mb-6 sm:mb-8 px-2">
            {t('about.location')}
          </p>
        </div>

        {/* Tabs Navigation - Optimized for mobile */}
        <div className="flex flex-wrap justify-center gap-1 sm:gap-2 mb-6 sm:mb-8 px-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap text-xs sm:text-sm ${
                activeTab === tab.id
                  ? 'bg-neutral-900 text-white'
                  : 'bg-white/80 text-neutral-700 hover:bg-white/90 backdrop-blur-sm'
              }`}
            >
              <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center">
                <i className={tab.icon}></i>
              </div>
              <span className="font-medium">{t(`about.tabs.${tab.key}`)}</span>
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="max-w-4xl mx-auto px-2 sm:px-0">
          {activeTab === 'mission' && (
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-neutral-900 text-white rounded-full">
                  <i className="ri-heart-line text-sm sm:text-base"></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900">
                  {t('about.mission.title')}
                </h3>
              </div>
              
              <p className="text-neutral-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
                {t('about.mission.description')}
              </p>

              <div className="space-y-3 sm:space-y-4">
                <h4 className="font-semibold text-neutral-900 text-base sm:text-lg">
                  {t('about.mission.goals.title')}
                </h4>
                <ul className="space-y-2 sm:space-y-3">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.mission.goals.proclaim')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.mission.goals.disciples')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.mission.goals.spirituality')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.mission.goals.sacraments')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.mission.goals.community')}
                    </span>
                  </li>
                </ul>
              </div>

              <blockquote className="border-l-4 border-neutral-900 pl-4 sm:pl-6 py-3 sm:py-4 bg-white rounded-r-lg mt-4 sm:mt-6">
                <p className="text-sm sm:text-base md:text-lg italic text-neutral-700 mb-2 leading-relaxed">
                  "{t('about.mission.quote')}"
                </p>
                <cite className="text-xs sm:text-sm font-medium text-neutral-900">
                  {t('about.mission.reference')}
                </cite>
              </blockquote>
            </div>
          )}

          {activeTab === 'vision' && (
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-neutral-900 text-white rounded-full">
                  <i className="ri-eye-line text-sm sm:text-base"></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900">
                  {t('about.vision.title')}
                </h3>
              </div>
              
              <p className="text-neutral-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
                {t('about.vision.description')}
              </p>

              <div className="space-y-3 sm:space-y-4">
                <h4 className="font-semibold text-neutral-900 text-base sm:text-lg">
                  {t('about.vision.goals.title')}
                </h4>
                <ul className="space-y-3 sm:space-y-4">
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.vision.goals.growth')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.vision.goals.impact')}
                    </span>
                  </li>
                  <li className="flex items-start gap-2 sm:gap-3">
                    <div className="w-4 h-4 sm:w-5 sm:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <span className="text-neutral-700 text-xs sm:text-sm md:text-base leading-relaxed">
                      {t('about.vision.goals.unity')}
                    </span>
                  </li>
                </ul>
              </div>

              <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-white rounded-lg border-l-4 border-neutral-900">
                <p className="text-neutral-700 text-sm sm:text-base md:text-lg font-medium italic leading-relaxed">
                  {t('about.vision.commitment')}
                </p>
              </div>
            </div>
          )}

          {activeTab === 'values' && (
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-neutral-900 text-white rounded-full">
                  <i className="ri-book-open-line text-sm sm:text-base"></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900">
                  {t('about.values.title')}
                </h3>
              </div>
              
              <p className="text-neutral-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
                {t('about.values.description')}
              </p>

              <h4 className="font-semibold text-neutral-900 text-base sm:text-lg mb-3 sm:mb-4">
                {t('about.values.subtitle')}
              </h4>

              <div className="space-y-3 sm:space-y-6">
                {/* Original 10 beliefs */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.god.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.god.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.god.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.jesus.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.jesus.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.jesus.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.holySpirit.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.holySpirit.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.holySpirit.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.church.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.church.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.church.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.salvation.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.salvation.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.salvation.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.humanity.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.humanity.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.humanity.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.bible.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.bible.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.bible.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.kingdom.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.kingdom.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.kingdom.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.judgment.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.judgment.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.judgment.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.eternalLife.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.eternalLife.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.eternalLife.reference')}
                  </cite>
                </div>

                {/* New beliefs k through p */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.christianExperience.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.christianExperience.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.christianExperience.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.spiritTestimony.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.spiritTestimony.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.spiritTestimony.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.christianPerfection.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.christianPerfection.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.christianPerfection.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.unityBelievers.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.unityBelievers.content')}
                  </p>
                  <cite className="text-xs text-neutral-600 italic">
                    {t('about.values.beliefs.unityBelievers.reference')}
                  </cite>
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.socialTransformation.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.socialTransformation.content')}
                  </p>
                  {t('about.values.beliefs.socialTransformation.reference') && (
                    <cite className="text-xs text-neutral-600 italic">
                      {t('about.values.beliefs.socialTransformation.reference')}
                    </cite>
                  )}
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.christianLife.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.christianLife.content')}
                  </p>
                  {t('about.values.beliefs.christianLife.reference') && (
                    <cite className="text-xs text-neutral-600 italic">
                      {t('about.values.beliefs.christianLife.reference')}
                    </cite>
                  )}
                </div>

                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <h5 className="font-semibold text-neutral-900 mb-1 sm:mb-2 text-sm sm:text-base">
                    {t('about.values.beliefs.lastThings.title')}
                  </h5>
                  <p className="text-neutral-700 text-xs sm:text-sm md:text-base mb-1 sm:mb-2 leading-relaxed">
                    {t('about.values.beliefs.lastThings.content')}
                  </p>
                  {t('about.values.beliefs.lastThings.reference') && (
                    <cite className="text-xs text-neutral-600 italic">
                      {t('about.values.beliefs.lastThings.reference')}
                    </cite>
                  )}
                </div>

                {/* Social Declaration */}
                <div className="bg-neutral-50 rounded-lg p-4 sm:p-6 border-l-4 border-neutral-900 mt-6 sm:mt-8">
                  <h5 className="font-bold text-neutral-900 mb-2 sm:mb-3 text-base sm:text-lg">
                    {t('about.values.socialDeclaration.title')}
                  </h5>
                  <p className="text-neutral-700 text-sm sm:text-base leading-relaxed">
                    {t('about.values.socialDeclaration.content')}
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'history' && (
            <div className="bg-white/95 backdrop-blur-sm rounded-lg p-4 sm:p-6 md:p-8 shadow-lg">
              <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="w-8 h-8 sm:w-10 sm:h-10 flex items-center justify-center bg-neutral-900 text-white rounded-full">
                  <i className="ri-star-line text-sm sm:text-base"></i>
                </div>
                <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-neutral-900">
                  {t('about.history.title')}
                </h3>
              </div>
              
              <p className="text-neutral-700 text-sm sm:text-base md:text-lg leading-relaxed mb-4 sm:mb-6">
                {t('about.history.description')}
              </p>

              <div className="grid gap-3 sm:gap-4 md:grid-cols-2">
                {/* Centrality of Christ */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-heart-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.christ.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.christ.description')}
                  </p>
                </div>

                {/* Biblical Authority */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-book-open-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.bible.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.bible.description')}
                  </p>
                </div>

                {/* Prayer and Spirit */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-hands-pray-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.prayer.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.prayer.description')}
                  </p>
                </div>

                {/* Sincere Worship */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-music-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.worship.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.worship.description')}
                  </p>
                </div>

                {/* Intentional Discipleship */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-group-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.discipleship.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.discipleship.description')}
                  </p>
                </div>

                {/* Evangelization and Mission */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-earth-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.evangelism.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.evangelism.description')}
                  </p>
                </div>

                {/* Love and Service */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-hand-heart-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.service.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.service.description')}
                  </p>
                </div>

                {/* Unity and Community */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-team-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.unity.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.unity.description')}
                  </p>
                </div>

                {/* Integrity and Transparency */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-shield-check-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.integrity.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.integrity.description')}
                  </p>
                </div>

                {/* Generous Stewardship */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-gift-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.stewardship.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.stewardship.description')}
                  </p>
                </div>

                {/* Justice and Mercy */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-scales-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.justice.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.justice.description')}
                  </p>
                </div>

                {/* Multiplication */}
                <div className="bg-white rounded-lg p-3 sm:p-4 border-l-4 border-neutral-900">
                  <div className="flex items-center gap-2 sm:gap-3 mb-1 sm:mb-2">
                    <div className="w-5 h-5 sm:w-6 sm:h-6 flex items-center justify-center">
                      <i className="ri-plant-fill text-neutral-900 text-sm sm:text-base"></i>
                    </div>
                    <h5 className="font-semibold text-neutral-900 text-sm sm:text-base">
                      {t('about.history.values.multiplication.title')}
                    </h5>
                  </div>
                  <p className="text-neutral-700 text-xs sm:text-sm leading-relaxed">
                    {t('about.history.values.multiplication.description')}
                  </p>
                </div>
              </div>
            </div>
          )}

        </div>

        {/* Original Quote Section */}
        <div className="max-w-4xl mx-auto mt-8 sm:mt-12 px-2 sm:px-0">
          <blockquote className="border-l-4 border-neutral-900 pl-4 sm:pl-6 py-3 sm:py-4 bg-white/95 backdrop-blur-sm rounded-r-lg shadow-lg">
            <p className="text-sm sm:text-base lg:text-lg italic text-neutral-700 mb-2 leading-relaxed">
              "{t('about.quote')}"
            </p>
            <cite className="text-xs sm:text-sm font-medium text-neutral-900">
              {t('about.reference')}
            </cite>
          </blockquote>
        </div>
      </div>
    </section>
  );
}
